import { motion } from "framer-motion";
import { BookOpen, Instagram, Youtube } from "lucide-react";

const PODCAST_ICON = "https://static.prod-images.emergentagent.com/jobs/d354418a-0de3-4cd0-a1c7-b2b40d50fad0/images/15059393bd7f1ed605a4c0702d0a77b24a39d6ea0df3d9f14d6889e6186dfd71.png";
const VLOG_ICON = "https://static.prod-images.emergentagent.com/jobs/d354418a-0de3-4cd0-a1c7-b2b40d50fad0/images/5e4881a3a3c37cfd1f73fe3a08f1eb4eb382e64745488ee0123d989ed96a6442.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6 },
  }),
};

export default function HighOnLifePage() {
  return (
    <div className="pt-28 lg:pt-44" data-testid="high-on-life-page">
      {/* Hero */}
      <section className="py-10 md:py-14 bg-gradient-to-br from-peachlight via-warmcream to-peachlight">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="font-accent text-2xl text-sunset mb-1">the good vibes hub</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4">
                High on Life
              </h1>
              <p className="text-charcoal/60 max-w-xl leading-relaxed">
                Real talk about happiness, wellness and everything that makes life worth living.
                Podcasts, blogs, vlogs and good energy — all in one place.
              </p>
            </div>
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_0d39ab76-9d5c-4abc-8302-8d3f959ae156/artifacts/9t3xpvew_IMG-20251222-WA0048.jpg"
                alt="Jax surfing"
                className="max-w-sm w-full aspect-[3/4] rounded-3xl object-cover"
                data-testid="high-on-life-profile"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content Hub */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Podcast */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={0}
              className="bg-warmcream rounded-3xl p-8 border border-sage/10 hover:shadow-lg transition-all duration-300"
              data-testid="podcast-section"
            >
              <div className="flex justify-center mb-6">
                <img src={PODCAST_ICON} alt="Podcast" className="w-20 h-20 object-contain illustration-transparent" />
              </div>
              <h3 className="font-display text-xl mb-3">Podcast</h3>
              <p className="text-charcoal/60 text-sm leading-relaxed mb-6">
                Honest conversations about happiness, wellness, surfing life and finding your groove.
                Coming soon to your favourite podcast platforms.
              </p>
              <span className="inline-flex items-center gap-1.5 bg-sage/10 text-sage text-xs font-medium px-3 py-1.5 rounded-full">
                Coming Soon
              </span>
            </motion.div>

            {/* Vlog / YouTube */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
              className="bg-warmcream rounded-3xl p-8 border border-sage/10 hover:shadow-lg transition-all duration-300"
              data-testid="vlog-section"
            >
              <div className="flex justify-center mb-6">
                <img src={VLOG_ICON} alt="Vlog" className="w-20 h-20 object-contain illustration-transparent" />
              </div>
              <h3 className="font-display text-xl mb-3">Vlog</h3>
              <p className="text-charcoal/60 text-sm leading-relaxed mb-6">
                Day-in-the-life, Cornwall adventures, hair transformations, dance sessions and sea swims.
                Subscribe and come along for the ride.
              </p>
              <a
                href="https://youtube.com/@justjjax"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-sunset/10 text-sunset text-xs font-medium px-3 py-1.5 rounded-full hover:bg-sunset/20 transition-colors"
                data-testid="vlog-youtube-link"
              >
                <Youtube size={12} /> YouTube Channel
              </a>
            </motion.div>

            {/* Instagram */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
              className="bg-warmcream rounded-3xl p-8 border border-sage/10 hover:shadow-lg transition-all duration-300"
              data-testid="instagram-section"
            >
              <div className="flex justify-center mb-6">
                <Instagram className="text-charcoal/70 w-16 h-16" />
              </div>
              <h3 className="font-display text-xl mb-3">Instagram</h3>
              <p className="text-charcoal/60 text-sm leading-relaxed mb-6">
                Daily inspiration, behind-the-scenes, outfit inspo, surf clips and all the good stuff.
                Follow along and join the community.
              </p>
              <a
                href="https://www.instagram.com/justjjax/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-rose text-sunset text-xs font-medium px-3 py-1.5 rounded-full hover:bg-sunset/20 transition-colors"
                data-testid="instagram-link"
              >
                <Instagram size={12} /> @justjjax
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-8 bg-sage/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <a
              href="https://www.instagram.com/justjjax/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-charcoal/60 hover:text-sunset transition-colors text-sm font-medium"
              data-testid="social-instagram"
            >
              <Instagram size={18} /> Instagram
            </a>
            <span className="text-charcoal/20">|</span>
            <a
              href="https://youtube.com/@justjjax"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-charcoal/60 hover:text-sunset transition-colors text-sm font-medium"
              data-testid="social-youtube"
            >
              <Youtube size={18} /> YouTube
            </a>
            <span className="text-charcoal/20">|</span>
            <a
              href="https://www.facebook.com/profile.php?id=61550018370626"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-charcoal/60 hover:text-sunset transition-colors text-sm font-medium"
              data-testid="social-facebook"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </a>
            <span className="text-charcoal/20">|</span>
            <a
              href="https://tiktok.com/@Just_jjax"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-charcoal/60 hover:text-sunset transition-colors text-sm font-medium"
              data-testid="social-tiktok"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.3 0 .59.05.86.12V9.01a6.32 6.32 0 00-.86-.06 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.28 8.28 0 004.76 1.5V6.8a4.84 4.84 0 01-1-.11z"/></svg>
              TikTok
            </a>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="text-sage" size={20} />
              <p className="font-accent text-xl text-sunset">read</p>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Blog</h2>
          </div>

          <div className="bg-warmcream rounded-3xl border border-sage/10 py-16 px-6 text-center" data-testid="blog-coming-soon">
            <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center mx-auto mb-5">
              <BookOpen size={28} className="text-sage" />
            </div>
            <h3 className="font-display text-2xl text-black mb-3">Coming Soon</h3>
            <p className="text-charcoal/60 text-sm max-w-md mx-auto">
              The blog is on its way — thoughts on happiness, hair tips, Cornwall life and everything
              in between. Follow @justjjax to know when the first posts drop.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
